import IconLayoutCorner from '@/assets/icons/icon-layout-corner.svg?react';
import NodePositionSettingsV2 from '@/components/dropdowns/Position/NodePositionSettingsV2';
import type { PaddingMarginValues, PositionControlProps } from '@/types/layout';
import { mapMarginPaddingArrayToObject } from '@/utils/helperFunc';
import { useInspectorNode } from '@/hooks/useInspectorNode';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { NodeMarginSettingsV2 } from '../PaddingMarginFields/NodeMarginSettingsV2';
import { NodePaddingSettingsV2 } from '../PaddingMarginFields/NodePaddingSettingsV2';
import { NodeRadiusSettingsV2 } from '../PaddingMarginFields/NodeRadiusSettingsV2';
import NodeVisibilitySettings from '../Visibility/NodeVisibilitySettings';
import { NodeZIndexSettingsV2 } from '../SliderField/NodeZIndexSettingsV2';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import type { TRBLNumberCOORDS } from '@builder4/shared/types/layout';
import { useAppSelector } from '@/store/hooks';
import { selectBreakpoint } from '@/store/system/selectors';
import { clampPadding } from '@builder4/shared/components/Page';
import { NodeDimensionSettings } from '../DimensionsFields/NodeDimensionSettings';
import NodeMaxWidthSettings from '../SliderField/NodeMaxWidthSettings';
import { Switch } from '@/components/inspector-fields/Switch/Switch';
import type { ContainerProps } from '@builder4/shared/components/Container';
import {
  MAX_PADDING,
  TEXT_NODE_NAME,
  CONTENT_BLOCK_NAME,
  IMAGE_NODE_NAME,
  FORM_NODE_NAME,
  VIDEO_NODE_NAME,
  POPUP_NODE_NAME,
} from '@builder4/shared/utils/constants';

const DEFAULT_TRBL: TRBLNumberCOORDS = [0, 0, 0, 0];

interface LayoutElementProps {
  className?: string;
  onClick?: () => void;
  state?: 'margin' | 'padding' | 'corner' | 'default';
  visibleLayout?: boolean; // New prop to control visibility
  showMargin?: boolean;
  showPosition?: boolean;
}

const SIDES: (keyof PositionControlProps)[] = ['top', 'bottom', 'left', 'right'];
export const LayoutElementV2: React.FC<LayoutElementProps> = ({
  className = 'max-w-[216px]',
  onClick,
  state = 'default',
  visibleLayout = true, // Default to visibleLayout
  showMargin = true,
  showPosition = true,
}) => {
  const { props: nodeProps, node, id, actions } = useInspectorNode<Partial<ContainerProps>>();
  const displayName = node?.data.displayName || '';
  const borderRadius = nodeProps.borderRadius || DEFAULT_TRBL;
  const margin = nodeProps.margin || DEFAULT_TRBL;
  const padding = nodeProps.padding || DEFAULT_TRBL;
  const tabletProps = nodeProps.tablet;
  const mobileProps = nodeProps.mobile;

  const { getBPProp, setBPProp } = useBreakpointAwareProp(displayName);
  const position = getBPProp(nodeProps, 'position');
  const fillWidth = getBPProp(nodeProps, 'disableMaxWidth') ?? false;
  const breakpoint = useAppSelector(selectBreakpoint);
  const [type, setType] = useState<'margin' | 'padding' | 'corner' | 'default'>(state);

  const [stateMargin, setStateMargin] = useState<PaddingMarginValues>(
    mapMarginPaddingArrayToObject(getBPProp(nodeProps, 'margin') || [0, 0, 0, 0]),
  );
  const [statePadding, setStatePadding] = useState<PaddingMarginValues>(
    mapMarginPaddingArrayToObject(getBPProp(nodeProps, 'padding') || [0, 0, 0, 0]),
  );
  const [stateRadius, setStateRadius] = useState<PaddingMarginValues>(
    mapMarginPaddingArrayToObject(getBPProp(nodeProps, 'borderRadius') || [0, 0, 0, 0]),
  );

  useEffect(() => {
    setStateRadius(
      mapMarginPaddingArrayToObject(getBPProp(nodeProps, 'borderRadius') || [0, 0, 0, 0]),
    );
  }, [borderRadius, breakpoint, tabletProps, mobileProps]);

  useEffect(() => {
    setStateMargin(mapMarginPaddingArrayToObject(getBPProp(nodeProps, 'margin') || [0, 0, 0, 0]));
  }, [margin, breakpoint, tabletProps, mobileProps]);

  useEffect(() => {
    const paddingValue = getBPProp(nodeProps, 'padding') || [0, 0, 0, 0];
    const value = mapMarginPaddingArrayToObject(
      clampPadding(paddingValue, MAX_PADDING[breakpoint]),
    );
    setStatePadding(value);
  }, [padding, breakpoint, tabletProps, mobileProps]);

  const handleClick = () => {
    onClick?.();
  };

  return (
    <div onClick={handleClick} className={clsx('flex flex-col gap-3', className)}>
      <p className="pb-1 text-xs font-semibold text-neutral-900">Layout</p>

      {visibleLayout && (
        <div
          className={clsx('relative h-[124px] w-full rounded-[20px] border', {
            'bg-neutral-10 border-neutral-100': type !== 'margin',
            'border-dashed border-indigo-300 bg-indigo-50': type === 'margin',
          })}
        >
          <div className="h-full w-full">
            {state !== 'margin' && (
              <>
                <p
                  className={clsx(
                    'absolute top-[0px] left-[0px] h-7 w-7 p-1.5 text-center text-[10px]',
                    {
                      'font-semibold text-neutral-900': type === 'corner',
                      'text-neutral-300': type !== 'corner',
                    },
                  )}
                >
                  {stateRadius?.top}
                </p>

                <p
                  className={clsx(
                    'absolute top-[0px] right-[0px] h-7 w-7 p-1.5 text-center text-[10px]',
                    {
                      'font-semibold text-neutral-900': type === 'corner',
                      'text-neutral-300': type !== 'corner',
                    },
                  )}
                >
                  {stateRadius?.right}
                </p>

                <p
                  className={clsx(
                    'absolute bottom-[0px] left-[0px] h-7 w-7 p-1.5 text-center text-[10px]',
                    {
                      'font-semibold text-neutral-900': type === 'corner',
                      'text-neutral-300': type !== 'corner',
                    },
                  )}
                >
                  {stateRadius?.left}
                </p>

                <p
                  className={clsx(
                    'absolute right-[0px] bottom-[0px] h-7 w-7 p-1.5 text-center text-[10px]',
                    {
                      'font-semibold text-neutral-900': type === 'corner',
                      'text-neutral-300': type !== 'corner',
                    },
                  )}
                >
                  {stateRadius?.bottom}
                </p>
              </>
            )}

            {SIDES.map((side) => {
              const positionMap: Record<keyof PositionControlProps, string> = {
                top: 'absolute w-full justify-center',
                bottom: 'absolute bottom-0 w-full justify-center',
                left: 'absolute left-0 h-full items-center',
                right: 'absolute right-0 h-full items-center',
              };

              const value = stateMargin?.[side];

              return (
                <div key={side} className={clsx('flex', positionMap[side])}>
                  <p
                    className={clsx(
                      'flex h-7 w-7 items-center justify-center p-1.5 text-[10px] font-medium',
                      {
                        'text-neutral-500': type === 'default',
                        'font-semibold text-neutral-900': type === 'margin',
                        'text-neutral-300': type !== 'default' && state !== 'margin',
                      },
                    )}
                  >
                    {value}
                  </p>
                </div>
              );
            })}

            <div className="absolute flex h-full w-full items-center justify-center">
              <div
                className={clsx('relative h-16 w-40 rounded-lg border', {
                  'bg-neutral-10 border-neutral-100': type !== 'padding',
                  'border-dashed border-indigo-300 bg-indigo-50': type === 'padding',
                })}
              >
                {(['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const).map((pos) => {
                  const rotateMap = {
                    'top-left': 'rotate-270 top-[-1px] left-[-1px]',
                    'top-right': 'rotate-0 top-[-1px] right-[-1px]',
                    'bottom-left': 'rotate-180 bottom-[-1px] left-[-1px]',
                    'bottom-right': 'rotate-90 bottom-[-1px] right-[-1px]',
                  };
                  return (
                    <IconLayoutCorner
                      key={pos}
                      className={clsx(
                        'absolute h-4 w-4',
                        rotateMap[pos],
                        type === 'corner' ? 'text-indigo-300' : 'text-[#AEB3B9]',
                      )}
                    />
                  );
                })}

                {(['top', 'bottom', 'left', 'right'] as const).map((side) => {
                  const positionMap: Record<'top' | 'bottom' | 'left' | 'right', string> = {
                    top: 'absolute w-full justify-center',
                    bottom: 'absolute bottom-0 w-full justify-center',
                    left: 'absolute left-0 h-full items-center',
                    right: 'absolute right-0 h-full items-center',
                  };

                  const value = statePadding?.[side];

                  return (
                    <div key={side} className={clsx('flex', positionMap[side])}>
                      <p
                        className={clsx(
                          'flex h-6 w-7 items-center justify-center p-1.5 text-[10px] font-medium',
                          {
                            'text-neutral-500': type === 'default',
                            'font-semibold text-neutral-900': type === 'padding',
                            'text-neutral-300': type !== 'default' && state !== 'padding',
                          },
                        )}
                      >
                        {value}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="absolute flex h-full w-full items-center justify-center">
              <div
                className={clsx('absolute h-5 w-12 rounded border', {
                  'bg-neutral-10 border-neutral-200': type !== 'default',
                  'border-indigo-300 bg-indigo-50': type === 'default',
                })}
              />
            </div>
          </div>
        </div>
      )}

      {showMargin && (
        <NodeMarginSettingsV2
          value={stateMargin}
          onFocus={() => setType('margin')}
          onBlur={() => setType('default')}
        />
      )}
      <NodePaddingSettingsV2
        value={statePadding}
        onFocus={() => setType('padding')}
        onBlur={() => setType('default')}
      />
      <NodeRadiusSettingsV2
        value={stateRadius}
        onFocus={() => setType('corner')}
        onBlur={() => setType('default')}
      />
      {showPosition && (
        <>
          <NodePositionSettingsV2 />
          {position !== 'relative' && <NodeZIndexSettingsV2 />}
        </>
      )}
      <NodeVisibilitySettings />
      <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
      <p className="pb-1 text-xs font-semibold text-neutral-900">Dimensions</p>
      <NodeDimensionSettings className="w-full max-w-[216px]" />
      {displayName === CONTENT_BLOCK_NAME && (
        <Switch
          checked={fillWidth}
          onCheckedChange={(checked) => {
            actions.setProp(id, (props: Partial<ContainerProps>) => {
              setBPProp(props, 'disableMaxWidth', checked);
            });
          }}
          label="Fill Width"
        />
      )}
      {hasMaxWidth(displayName) && (displayName !== CONTENT_BLOCK_NAME || !fillWidth) && (
        <NodeMaxWidthSettings />
      )}
    </div>
  );
};

const hasMaxWidth = (displayName: string) => {
  const allowedNodes = [
    TEXT_NODE_NAME,
    CONTENT_BLOCK_NAME,
    IMAGE_NODE_NAME,
    FORM_NODE_NAME,
    VIDEO_NODE_NAME,
    POPUP_NODE_NAME,
  ];
  return allowedNodes.includes(displayName);
};
